/**
 * Cluster GEO-DATA Fase C — CapPicker
 *
 * Picker IBRIDO per il CAP italiano: combobox a tendina con i CAP del
 * comune corrente + sblocco MANUALE sempre disponibile (per grandi
 * città dove il dataset può essere incompleto, o CAP nuovi).
 *
 * UX:
 *   • Se viene passato `comune` come oggetto con `cap: []`, mostra
 *     un Select dropdown con quei CAP.
 *   • Pulsante "Manuale" affianco al picker per passare a un Input libero
 *     (e tornare al dropdown). Stato "manual" persiste localmente.
 *   • Se non c'è un comune (estero o non selezionato), parte direttamente
 *     in modalità manuale.
 *
 * Props:
 *   value      (string) CAP corrente
 *   onChange   (fn)     callback(newCap)
 *   comune     (object|null) oggetto comune (con `cap: []`) per il dropdown
 *   disabled   (bool)
 *   testId     (string)
 *   placeholder
 */
import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Pencil, ListChecks } from "lucide-react";

export default function CapPicker({
  value, onChange, comune, disabled, testId, placeholder = "CAP",
}) {
  const caps = (comune && Array.isArray(comune.cap)) ? comune.cap : [];
  const hasDropdown = caps.length > 0;

  // Manual = true se non c'è dropdown disponibile, oppure se il valore
  // corrente non è nei CAP del comune corrente (= "fuori lista")
  const [manual, setManual] = useState(() => {
    if (!hasDropdown) return true;
    if (value && !caps.includes(value)) return true;
    return false;
  });

  // Quando cambia il comune, riprova ad allineare il modo al dataset
  useEffect(() => {
    if (!hasDropdown) {
      setManual(true);
    } else if (value && !caps.includes(value)) {
      setManual(true);
    } else {
      setManual(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comune?.code_belfiore]);

  return (
    <div className="flex gap-1 items-center">
      {manual ? (
        <Input
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value.replace(/\s+/g, ""))}
          maxLength={5}
          inputMode="numeric"
          placeholder={placeholder}
          disabled={disabled}
          className="font-mono"
          data-testid={testId ? `${testId}-manual-input` : undefined}
        />
      ) : (
        <Select
          value={value || ""}
          onValueChange={(v) => onChange?.(v)}
          disabled={disabled}
        >
          <SelectTrigger
            className="font-mono"
            data-testid={testId ? `${testId}-select` : undefined}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {caps.map((c) => (
              <SelectItem key={c} value={c}
                          data-testid={testId ? `${testId}-opt-${c}` : undefined}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {hasDropdown && (
        <Button
          type="button" size="sm" variant="ghost"
          className={cn("shrink-0 px-2 h-9", manual && "text-amber-700")}
          onClick={() => setManual((m) => !m)}
          title={manual
            ? `Torna alla lista CAP del comune (${caps.length} disponibili)`
            : "Inserimento manuale (per CAP fuori lista)"}
          disabled={disabled}
          data-testid={testId ? `${testId}-toggle-manual` : undefined}
        >
          {manual ? <ListChecks className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
        </Button>
      )}
    </div>
  );
}
